/**
 * PendingJobsContext
 * Provides shared state for pending document processing jobs.
 * Polls for job status updates and triggers callbacks on completion.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { addApi, ProcessingJob } from '@/services/addApi';
import { documentsApi } from '@/services/documentsApi';
import { useAuthContext } from '@/contexts/AuthContext';
import { getUserIdentityId } from '@/lib/awsClients';
import { createLogger } from '@/lib/logger';
import { PollingTimer } from '@/lib/polling';

const log = createLogger('PendingJobsContext');

/** Retention time for completed/failed job notifications (5 minutes) */
const NOTIFICATION_RETENTION_MS = 5 * 60 * 1000;

/** A notification item representing a completed or failed job */
export interface NotificationItem {
  job: ProcessingJob;
  type: 'completed' | 'failed';
  timestamp: number;
}

/** Request browser notification permission (no-op if unsupported or already decided) */
function requestNotificationPermission() {
  if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
    Notification.requestPermission();
  }
}

/** Send a browser notification (no-op if not granted) */
function sendNotification(title: string, body: string, url?: string) {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  const notification = new Notification(title, {
    body,
    icon: '/logo192.png',
  });
  if (url) {
    notification.onclick = () => {
      window.focus();
      window.location.href = url;
    };
  }
}

interface PendingJobsContextValue {
  /** List of all pending/in-progress jobs */
  pendingJobs: ProcessingJob[];
  /** Whether we're currently polling */
  isPolling: boolean;
  /** Manually refresh the jobs list */
  refreshJobs: () => Promise<void>;
  /** Add a job to track (called after submitAsyncJob) */
  addJob: (job: ProcessingJob) => void;
  /** Callback when a job completes successfully */
  onJobComplete?: (job: ProcessingJob) => void;
  /** Set the completion callback */
  setOnJobComplete: (callback: (job: ProcessingJob) => void) => void;
  /** Recently completed/failed jobs for the notification bell */
  notifications: NotificationItem[];
  /** Dismiss a single notification by jobId */
  dismissNotification: (jobId: string) => void;
  /** Dismiss all notifications */
  dismissAllNotifications: () => void;
}

const PendingJobsContext = createContext<PendingJobsContextValue | undefined>(undefined);

interface PendingJobsProviderProps {
  children: React.ReactNode;
}

export const PendingJobsProvider: React.FC<PendingJobsProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuthContext();
  const navigate = useNavigate();
  const [pendingJobs, setPendingJobs] = useState<ProcessingJob[]>([]);
  const [isPolling, setIsPolling] = useState(false);
  const [identityReady, setIdentityReady] = useState(false);
  const pollerRef = useRef<PollingTimer | null>(null);
  const onJobCompleteRef = useRef<((job: ProcessingJob) => void) | undefined>();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  // Prune expired notifications every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setNotifications(prev => {
        const now = Date.now();
        const filtered = prev.filter(n => now - n.timestamp < NOTIFICATION_RETENTION_MS);
        return filtered.length === prev.length ? prev : filtered;
      });
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const dismissNotification = useCallback((jobId: string) => {
    setNotifications(prev => prev.filter(n => n.job.jobId !== jobId));
  }, []);

  const dismissAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Resolve identityId and configure addApi when authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setIdentityReady(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const identityId = await getUserIdentityId();
        if (!cancelled) {
          addApi.setIdentityId(identityId);
          documentsApi.setIdentityId(identityId);
          setIdentityReady(true);
          log.debug('Identity configured for job polling', { identityId });
        }
      } catch (err) {
        log.error('Failed to resolve identityId', err as Error);
      }
    })();

    return () => { cancelled = true; };
  }, [isAuthenticated]);

  /**
   * Fetch pending jobs from API and merge with locally tracked jobs.
   * This recovers any in-progress jobs after a page refresh.
   */
  const fetchPendingJobs = useCallback(async () => {
    if (!isAuthenticated) {
      setPendingJobs([]);
      return;
    }

    try {
      const apiJobs = await addApi.listPendingJobs();
      log.debug('Fetched pending jobs from API', { count: apiJobs.length });

      // Merge API jobs with locally tracked jobs
      setPendingJobs(prev => {
        const apiJobIds = new Set(apiJobs.map(j => j.jobId));

        // Keep local jobs that aren't in API response (newer, not yet synced)
        const localOnlyJobs = prev.filter(j => !apiJobIds.has(j.jobId));

        // API jobs take priority, then add local-only jobs
        const merged = [...apiJobs, ...localOnlyJobs];

        log.debug('Merged jobs', {
          apiCount: apiJobs.length,
          localOnlyCount: localOnlyJobs.length,
          mergedCount: merged.length,
        });

        return merged;
      });
    } catch (error) {
      log.error('Failed to fetch pending jobs', error as Error);
    }
  }, [isAuthenticated]);

  // Use ref to avoid stale closure in polling interval
  const pendingJobsRef = useRef<ProcessingJob[]>([]);
  pendingJobsRef.current = pendingJobs;

  /**
   * Check status of all tracked jobs
   */
  const checkJobStatuses = useCallback(async () => {
    const currentJobs = pendingJobsRef.current;

    if (!isAuthenticated || currentJobs.length === 0) {
      return;
    }

    log.debug('Checking job statuses', { count: currentJobs.length });

    const updatedJobs: ProcessingJob[] = [];
    const completedJobs: ProcessingJob[] = [];
    const failedJobs: ProcessingJob[] = [];

    for (const job of currentJobs) {
      try {
        const updated = await addApi.getJobStatus(job.jobId);

        if (updated.status === 'ready') {
          completedJobs.push(updated);
        } else if (updated.status === 'failed') {
          failedJobs.push(updated);
        } else {
          updatedJobs.push(updated);
        }
      } catch (error) {
        log.warn('Failed to check job status', { jobId: job.jobId, error });
        // Keep the job in the list, it might recover
        updatedJobs.push(job);
      }
    }

    // Update state with remaining jobs
    setPendingJobs(updatedJobs);

    // Add completed/failed jobs to notification bell
    if (completedJobs.length > 0 || failedJobs.length > 0) {
      setNotifications(prev => [
        ...completedJobs.map(job => ({ job, type: 'completed' as const, timestamp: Date.now() })),
        ...failedJobs.map(job => ({ job, type: 'failed' as const, timestamp: Date.now() })),
        ...prev,
      ]);
    }

    // Handle completed jobs
    for (const job of completedJobs) {
      log.info('Job completed', { jobId: job.jobId, documentId: job.documentId });

      toast.success('Document Ready!', {
        description: `"${job.templateName}" for ${job.projectName} is ready to view.`,
        duration: 5000,
        action: {
          label: 'View Project',
          onClick: () => navigate(`/projects?project=${job.projectId}`),
        },
      });

      sendNotification(
        'Document Ready!',
        `"${job.templateName}" for ${job.projectName} is ready to view.`,
        `/projects?project=${job.projectId}`
      );

      // Call completion callback
      if (onJobCompleteRef.current) {
        onJobCompleteRef.current(job);
      }
    }

    // Handle failed jobs
    for (const job of failedJobs) {
      log.error('Job failed', new Error(job.error || 'Unknown error'), { jobId: job.jobId });

      toast.error('Processing Failed', {
        description: job.error || `Failed to process "${job.templateName}". Please try again.`,
        duration: 8000,
      });

      sendNotification(
        'Processing Failed',
        job.error || `Failed to process "${job.templateName}".`
      );
    }
  }, [isAuthenticated, navigate]);

  /**
   * Start polling for job updates (with immediate first check).
   * Uses exponential backoff: 5s → 10s → 20s → … → 15 min cap.
   * Stops automatically after 15 minutes of continuous polling.
   */
  const startPolling = useCallback(() => {
    if (pollerRef.current?.isRunning) {
      return; // Already polling
    }

    log.debug('Starting job status polling with exponential backoff');
    setIsPolling(true);

    const poller = new PollingTimer({
      initialInterval: 5_000,
      maxInterval: 15 * 60 * 1_000,
      onTimeout: () => {
        log.warn('Job polling timed out after 15 minutes');
        setIsPolling(false);
        toast.warning('Still processing', {
          description: 'Your document is taking longer than expected. Please check back and retry if needed.',
          duration: 15_000,
        });
      },
    });
    pollerRef.current = poller;
    poller.start(checkJobStatuses);
  }, [checkJobStatuses]);

  /**
   * Stop polling
   */
  const stopPolling = useCallback(() => {
    if (pollerRef.current?.isRunning) {
      log.debug('Stopping job status polling');
      pollerRef.current.stop();
      setIsPolling(false);
    }
  }, []);

  /**
   * Manually refresh jobs list
   */
  const refreshJobs = useCallback(async () => {
    await fetchPendingJobs();
  }, [fetchPendingJobs]);

  /**
   * Add a job to track. Resets backoff to fast polling.
   */
  const addJob = useCallback((job: ProcessingJob) => {
    log.debug('Adding job to tracking', { jobId: job.jobId });
    setPendingJobs(prev => [job, ...prev]);
    requestNotificationPermission();

    // Reset backoff so the new job is checked quickly
    if (pollerRef.current?.isRunning) {
      pollerRef.current.reset();
    }
  }, []);

  /**
   * Set completion callback
   */
  const setOnJobComplete = useCallback((callback: (job: ProcessingJob) => void) => {
    onJobCompleteRef.current = callback;
  }, []);

  // Start/stop polling based on authentication, identity resolution, and pending jobs
  useEffect(() => {
    if (isAuthenticated && identityReady && pendingJobs.length > 0) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [isAuthenticated, identityReady, pendingJobs.length, startPolling, stopPolling]);

  // Fetch initial jobs when authenticated and identity is resolved
  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (isAuthenticated && identityReady && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchPendingJobs();
    } else if (!isAuthenticated) {
      hasFetchedRef.current = false;
      setPendingJobs([]);
      setNotifications([]);
      stopPolling();
    }
  }, [isAuthenticated, identityReady, fetchPendingJobs, stopPolling]);

  const value: PendingJobsContextValue = {
    pendingJobs,
    isPolling,
    refreshJobs,
    addJob,
    setOnJobComplete,
    notifications,
    dismissNotification,
    dismissAllNotifications,
  };

  return (
    <PendingJobsContext.Provider value={value}>
      {children}
    </PendingJobsContext.Provider>
  );
};

/**
 * Hook to access pending jobs context
 */
export const usePendingJobs = (): PendingJobsContextValue => {
  const context = useContext(PendingJobsContext);
  if (!context) {
    throw new Error('usePendingJobs must be used within a PendingJobsProvider');
  }
  return context;
};

export default PendingJobsContext;
